/**
 * Generate a poster (thumbnail) image from a video File, entirely client-side.
 * Returns a JPEG File suitable for upload, or null if a frame can't be captured
 * (best-effort — callers should save the video without a poster on null).
 *
 * Used at upload time so a stored poster is served as a small image, giving the
 * player an instant first paint instead of having to fetch the video to grab a
 * frame at runtime.
 */
/**
 * Capture a poster JPEG File from an already-loaded <video> element (used to
 * backfill posters for existing videos on view). Returns null if the frame can't
 * be read (e.g. a cross-origin/tainted canvas). Best-effort.
 */
export function capturePosterFromVideoElement(videoEl, { maxSize = 640, quality = 0.7 } = {}) {
  return new Promise((resolve) => {
    try {
      if (!videoEl || videoEl.readyState < 2) return resolve(null);
      const vw = videoEl.videoWidth || 640;
      const vh = videoEl.videoHeight || 360;
      const scale = Math.min(maxSize / vw, maxSize / vh, 1);
      const canvas = document.createElement('canvas');
      canvas.width = Math.max(1, Math.round(vw * scale));
      canvas.height = Math.max(1, Math.round(vh * scale));
      canvas.getContext('2d', { alpha: false }).drawImage(videoEl, 0, 0, canvas.width, canvas.height);
      canvas.toBlob(
        (blob) => resolve(blob ? new File([blob], 'poster-backfill.jpg', { type: 'image/jpeg' }) : null),
        'image/jpeg',
        quality
      );
    } catch (e) {
      resolve(null);
    }
  });
}

export async function generateVideoPosterFile(file, { seekTo = 0.1, maxSize = 640, quality = 0.7 } = {}) {
  if (!file || !(file instanceof Blob)) return null;
  if (file.type && !file.type.startsWith('video/')) return null;

  return new Promise((resolve) => {
    let settled = false;
    let objectUrl = null;
    const video = document.createElement('video');

    const finish = (result) => {
      if (settled) return;
      settled = true;
      clearTimeout(timeoutId);
      try { if (objectUrl) URL.revokeObjectURL(objectUrl); } catch (e) { /* ignore */ }
      resolve(result);
    };

    // Some codecs/large files never fire seeked — bail after a bounded wait.
    const timeoutId = setTimeout(() => finish(null), 8000);

    try {
      objectUrl = URL.createObjectURL(file);
      video.preload = 'metadata';
      video.muted = true;
      video.playsInline = true;
      video.crossOrigin = 'anonymous';
      video.src = objectUrl;

      video.onloadeddata = () => {
        try {
          const target = Math.min(seekTo, (video.duration || 1) / 2);
          video.currentTime = Number.isFinite(target) && target > 0 ? target : 0;
        } catch (e) {
          finish(null);
        }
      };

      video.onseeked = () => {
        try {
          const vw = video.videoWidth || 640;
          const vh = video.videoHeight || 360;
          const scale = Math.min(maxSize / vw, maxSize / vh, 1);
          const canvas = document.createElement('canvas');
          canvas.width = Math.max(1, Math.round(vw * scale));
          canvas.height = Math.max(1, Math.round(vh * scale));
          const ctx = canvas.getContext('2d', { alpha: false });
          ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
          canvas.toBlob((blob) => {
            if (!blob) return finish(null);
            const name = `poster-${(file.name || 'video').replace(/\.[^.]+$/, '')}.jpg`;
            finish(new File([blob], name, { type: 'image/jpeg' }));
          }, 'image/jpeg', quality);
        } catch (e) {
          finish(null);
        }
      };

      video.onerror = () => finish(null);
    } catch (e) {
      finish(null);
    }
  });
}

/**
 * Capture several frames spread across a video, as base64 JPEGs, for content
 * screening. Uses one video element and seeks it repeatedly rather than
 * building one element per frame.
 *
 * Best-effort: returns whatever frames it managed to read, possibly none.
 */
export async function generateVideoFrames(file, { count = 8, maxSize = 640, quality = 0.6 } = {}) {
  if (!file || !(file instanceof Blob)) return [];
  if (file.type && !file.type.startsWith('video/')) return [];

  return new Promise((resolve) => {
    const frames = [];
    let settled = false;
    let objectUrl = null;
    let targets = [];
    let index = 0;
    const video = document.createElement('video');

    const finish = () => {
      if (settled) return;
      settled = true;
      clearTimeout(timeoutId);
      try { if (objectUrl) URL.revokeObjectURL(objectUrl); } catch (e) { /* ignore */ }
      resolve(frames);
    };

    // Whole-operation bound: a video that never seeks must not hang the upload.
    const timeoutId = setTimeout(finish, 20000);

    const seekNext = () => {
      if (index >= targets.length) return finish();
      try {
        video.currentTime = targets[index++];
      } catch (e) {
        finish();
      }
    };

    try {
      objectUrl = URL.createObjectURL(file);
      video.preload = 'metadata';
      video.muted = true;
      video.playsInline = true;
      video.crossOrigin = 'anonymous';
      video.src = objectUrl;

      video.onloadeddata = () => {
        const duration = Number.isFinite(video.duration) && video.duration > 0 ? video.duration : 0;
        if (!duration) return finish();
        // Skip the very start and end, which are often black.
        const span = duration * 0.9;
        const step = span / count;
        targets = Array.from({ length: count }, (_, i) => duration * 0.05 + step * i);
        seekNext();
      };

      video.onseeked = () => {
        try {
          const vw = video.videoWidth || 640;
          const vh = video.videoHeight || 360;
          const scale = Math.min(maxSize / vw, maxSize / vh, 1);
          const canvas = document.createElement('canvas');
          canvas.width = Math.max(1, Math.round(vw * scale));
          canvas.height = Math.max(1, Math.round(vh * scale));
          canvas.getContext('2d', { alpha: false }).drawImage(video, 0, 0, canvas.width, canvas.height);
          frames.push(canvas.toDataURL('image/jpeg', quality));
        } catch (e) {
          // A tainted canvas or a frame we can't read — keep what we have.
        }
        seekNext();
      };

      video.onerror = () => finish();
    } catch (e) {
      finish();
    }
  });
}
